import { createClient } from '@supabase/supabase-js';
export const SUPABASE_URL = 'https://dnbvsivbzbvzwqlxczzf.supabase.co';
// La anon key es pública por diseño de Supabase: su seguridad depende de
// que las políticas RLS de cada tabla sean correctas (ver RLS_USUARIOS_SQL),
// no de mantenerla en secreto.
export const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImRuYnZzaXZiemJ2endxbHhjenpmIiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODAwNTEzMzksImV4cCI6MjA5NTYyNzMzOX0.5vdjEZu9ukwtkQFJnkCXaBad6NGaxhtTdWVyB1ov9mE';
export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// ── TIEMPO REAL ─────────────────────────────────────────────────────────────
// Supabase avisa por websocket de cada insert/update/delete de las tablas que
// estén en la publicación `supabase_realtime` (ver sql/tiempo_real.sql). Así,
// cuando alguien guarda una orden desde otra computadora, a los demás les
// aparece sola sin recargar.
//
// UN SOLO CANAL POR TABLA, compartido por todos los que la escuchen.
//
// Antes cada llamada abría su propio canal. `app_state` la escuchan cinco
// sitios a la vez (las cuatro claves de useStoredState más el aviso de
// usuarios), así que un solo cambio en esa tabla llegaba CINCO veces a cada
// pestaña, y Supabase cobra por mensaje entregado. Con un canal compartido
// llega una vez y se reparte en el navegador, que no cuesta nada.
export type CambioRealtime = {
  tipo: 'INSERT' | 'UPDATE' | 'DELETE';
  fila: any;
  filaVieja: any;
};
type Oyente = (c: CambioRealtime) => void;
const canalesPorTabla = new Map<string, {
  canal: ReturnType<typeof supabase.channel>;
  oyentes: Set<Oyente>;
}>();

/**
 * Escucha los cambios de una tabla. Devuelve la función para dejar de
 * escuchar, que hay que llamar al desmontar. Varios sitios pueden escuchar la
 * misma tabla: comparten un único canal y el último en irse lo cierra.
 */
export function escucharTabla(tabla: string, alCambiar: Oyente): () => void {
  let entrada = canalesPorTabla.get(tabla);
  if (!entrada) {
    const oyentes = new Set<Oyente>();
    const canal = supabase.channel(`rt-${tabla}`).on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: tabla
    }, (msg: any) => {
      const cambio: CambioRealtime = {
        tipo: msg.eventType,
        fila: msg.new,
        filaVieja: msg.old
      };
      // Sobre una copia: un oyente puede darse de baja mientras se reparte.
      for (const oyente of [...oyentes]) {
        try {
          oyente(cambio);
        } catch (e) {
          // Que uno falle no puede dejar a los demás sin su aviso.
          console.warn(`[realtime] Un oyente de "${tabla}" falló:`, e);
        }
      }
    }).subscribe((estado: string) => {
      // Si la tabla no está en la publicación el canal se suscribe igual pero
      // no llega nada nunca; el aviso de error sí se ve aquí.
      if (estado === 'CHANNEL_ERROR' || estado === 'TIMED_OUT') {
        console.warn(`[realtime] No se pudo escuchar "${tabla}" (${estado}). ` + `Corre sql/tiempo_real.sql en el SQL Editor de Supabase.`);
      }
    });
    entrada = {
      canal,
      oyentes
    };
    canalesPorTabla.set(tabla, entrada);
  }
  entrada.oyentes.add(alCambiar);
  return () => {
    const actual = canalesPorTabla.get(tabla);
    if (!actual) return;
    actual.oyentes.delete(alCambiar);
    if (actual.oyentes.size === 0) {
      canalesPorTabla.delete(tabla);
      void supabase.removeChannel(actual.canal);
    }
  };
}

// ── FRENO DE EMERGENCIA CONTRA BUCLES DE ESCRITURA ──────────────────────────
//
// Un bucle de guardado no se nota en pantalla —todo "funciona"— pero cada
// escritura genera un aviso de tiempo real para cada pestaña conectada, y eso
// se paga. Ya pasó: un aviso que se guardaba, disparaba otro aviso, que se
// volvía a guardar, dejaba la app escribiendo dos veces y media por segundo,
// para siempre, sin que nadie tocara nada. Millones de mensajes en semanas.
//
// Esto es la red debajo del trapecio, no el arreglo: si una tabla pasa de
// LIMITE escrituras en un minuto, no hay trabajo humano que lo explique, así
// que se corta esa tabla y se avisa en vez de seguir gastando en silencio.
const LIMITE_ESCRITURAS_POR_MINUTO = 90;
const historialEscrituras = new Map<string, number[]>();
const frenadas = new Set<string>();

/**
 * ¿Se puede escribir en esta tabla/clave ahora? Devuelve false —y lo avisa una
 * sola vez— cuando el ritmo solo se explica por un bucle.
 */
export function permitirEscritura(clave: string): boolean {
  if (frenadas.has(clave)) return false;
  const ahora = Date.now();
  const marcas = (historialEscrituras.get(clave) ?? []).filter(t => ahora - t < 60000);
  marcas.push(ahora);
  historialEscrituras.set(clave, marcas);
  if (marcas.length <= LIMITE_ESCRITURAS_POR_MINUTO) return true;
  frenadas.add(clave);
  console.error(`[db] FRENO: "${clave}" se escribió ${marcas.length} veces en un minuto. ` + `Eso es un bucle, no trabajo de nadie. Se deja de escribir esa tabla ` + `hasta recargar la página, para no gastar el plan de tiempo real.`);
  alert(`El sistema detectó que "${clave}" se estaba guardando sin parar ` + `(${marcas.length} veces en un minuto).\n\n` + `Se detuvo el guardado de esa tabla para no agotar el plan de Supabase. ` + `Recarga la página. Si vuelve a pasar, avísale a quien mantiene el sistema.`);
  return false;
}

// Edge Functions: único camino para leer/escribir usuarios (ver
// RLS_USUARIOS_SQL — el cliente anónimo no tiene acceso directo a esa tabla).
export const LOGIN_FUNCTION_URL = `${SUPABASE_URL}/functions/v1/login`;
export const GUARDAR_USUARIO_FUNCTION_URL = `${SUPABASE_URL}/functions/v1/guardar-usuario`;
export const LISTAR_USUARIOS_FUNCTION_URL = `${SUPABASE_URL}/functions/v1/listar-usuarios`;

// Nombre de la tabla key-value usada para la persistencia remota.
export const APP_STATE_TABLE = 'app_state';
export type ConnectionStatus = 'connected' | 'no-table' | 'offline';

// SQL listo para copiar y crear la tabla en el editor SQL de Supabase.
export const APP_STATE_SQL = `create table if not exists app_state (
  key text primary key,
  value jsonb,
  updated_at timestamptz default now()
);

-- Permite leer/escribir con la llave anónima (prototipo).
alter table app_state enable row level security;

create policy "app_state acceso anon"
  on app_state for all
  to anon
  using (true)
  with check (true);`;
function classifyError(error: {
  code?: string;
  message?: string;
} | null): ConnectionStatus {
  if (!error) return 'connected';
  const code = error.code;
  const msg = error.message ?? '';
  if (code === '42P01' || code === 'PGRST205' || /could not find the table|does not exist|not.*in.*schema/i.test(msg)) {
    return 'no-table';
  }
  return 'offline';
}

// Comprueba en vivo si Supabase responde y si la tabla key-value existe.
export async function getConnectionStatus(): Promise<ConnectionStatus> {
  try {
    const {
      error
    } = await supabase.from(APP_STATE_TABLE).select('key').limit(1);
    return classifyError(error);
  } catch {
    return 'offline';
  }
}

// ── MODELO DE DATOS POR ENTIDAD ───────────────────────────────────────────────
// Tablas reales del dominio. Los nombres de columna van en snake_case y el
// código de la app mapea camelCase <-> snake_case al sincronizar.
export interface TableDef {
  name: string;
  label: string;
  sql: string;
}
export const MODEL_TABLES: TableDef[] = [{
  name: 'tipos_sucursal',
  label: 'Tipos de sucursal',
  // Catálogo editable de tipos (antes hardcodeado en data.ts como TIPOS).
  // El campo "tipo" de sucursales referencia el "nombre" de este catálogo.
  sql: `create table if not exists tipos_sucursal (
  id text primary key,
  nombre text not null unique,
  orden integer default 0,
  archivado boolean default false
);`
}, {
  name: 'sucursales',
  label: 'Sucursales',
  sql: `create table if not exists sucursales (
  id text primary key,
  nombre text not null,
  direccion text default '',
  tipo text default '', -- referencia "suave" a tipos_sucursal.nombre (sin FK, para no romper datos libres)
  creado bigint default (extract(epoch from now()) * 1000)::bigint,
  archivado boolean default false
);`
}, {
  name: 'usuarios',
  label: 'Usuarios',
  // NOTA DE SEGURIDAD: la columna es password_hash (bcrypt), nunca texto
  // plano. Esta tabla NO usa la política RLS genérica de abajo: ver
  // RLS_USUARIOS_SQL — el cliente anónimo no puede leer ni escribir aquí
  // directamente, solo a través de las Edge Functions login/guardar-usuario.
  sql: `create table if not exists usuarios (
  id text primary key,
  nombre text not null,
  correo text unique not null,
  password_hash text not null,
  rol text not null,
  sucursales_ids jsonb not null default '[]'::jsonb,
  archivado boolean default false
);`
}, {
  name: 'roles',
  label: 'Roles y permisos',
  sql: `create table if not exists roles (
  id text primary key,
  nombre text not null,
  descripcion text default '',
  permisos jsonb not null default '{}'::jsonb,
  archivado boolean default false
);`
}, {
  name: 'permisos',
  label: 'Permisos por módulo',
  // Tabla normalizada: un registro por (rol × módulo) con las tres acciones.
  // Módulos actuales del sistema: consumo, sucursales, reportes, alertas,
  // usuarios, roles, auditoria, configuracion.
  sql: `create table if not exists permisos (
  id text primary key,
  rol_id text references roles(id) on delete cascade,
  modulo text not null check (modulo in (
    'consumo','costoConsumo','sucursales','reportes','alertas',
    'usuarios','roles','auditoria','configuracion'
  )),
  leer boolean not null default false,
  editar boolean not null default false,
  eliminar boolean not null default false,
  unique (rol_id, modulo)
);`
}, {
  name: 'consumo',
  label: 'Consumo',
  // `contador` es texto: se anota a mano en cada lectura, no hay catálogo.
  sql: `create table if not exists consumo (
  id text primary key,
  sucursal_id text references sucursales(id) on delete cascade,
  contador text not null default '',
  fecha bigint not null,
  kwh numeric not null,
  archivado boolean default false
);`
}, {
  name: 'auditoria',
  label: 'Auditoría',
  sql: `create table if not exists auditoria (
  id bigint generated always as identity primary key,
  mensaje text not null,
  fecha timestamptz not null default now()
);`
}, {
  name: 'configuracion',
  label: 'Configuración',
  sql: `create table if not exists configuracion (
  id text primary key default 'default',
  perfil jsonb not null default '{}'::jsonb,
  notificaciones jsonb not null default '{}'::jsonb,
  preferencias jsonb not null default '{}'::jsonb
);`
}, {
  name: 'gas_clientes',
  label: 'Gas · Clientes',
  sql: `create table if not exists gas_clientes (
  id text primary key,
  codigo text default '',
  nombre text not null,
  rnc text default '',
  contacto text default '',
  telefono text default '',
  email text default '',
  direccion text default '',
  ubicacion text default '',
  tipo text default 'Comercio' check (tipo in ('Industria','Comercio','Residencial','Otro')),
  capacidad_tanque numeric default 0,
  estado text default 'activo' check (estado in ('activo','inactivo')),
  observaciones text default '',
  fecha_registro date default current_date,
  ultima_compra date,
  dias_sin_comprar integer,
  -- Datos del propietario/representante legal, para imprimir el contrato.
  representante text default '',
  representante_cedula text default '',
  representante_direccion text default ''
);`
}, {
  name: 'gas_contratos',
  label: 'Gas · Contratos',
  sql: `create table if not exists gas_contratos (
  id text primary key,
  numero_contrato text not null,
  cliente_id text references gas_clientes(id) on delete cascade,
  cliente_nombre text default '',
  fecha_inicio date,
  fecha_vencimiento date,
  precio_acordado numeric default 0,
  condiciones text default '',
  estado text default 'vigente' check (estado in ('vigente','vencido','cancelado')),
  firma_digital text,
  fecha_creacion date default current_date,
  -- Datos del/los cilindro(s), para imprimir el contrato legal completo.
  cantidad_tanques integer default 1,
  capacidad_galones integer,
  valor_tanque numeric,
  ciudad text default 'Santiago de los Caballeros'
);`
}, {
  name: 'gas_rutas',
  label: 'Gas · Rutas',
  sql: `create table if not exists gas_rutas (
  id text primary key,
  nombre text not null,
  zona text default '',
  supervisor text default '',
  -- Lista de clientes asignados a la ruta (clienteId, nombre, código,
  -- ubicación y estado de la visita); se guarda como jsonb porque el orden
  -- y el estado de visita cambian junto con la ruta, no son independientes.
  clientes jsonb not null default '[]'::jsonb,
  fecha_creacion date default current_date,
  dias_semana jsonb not null default '[]'::jsonb,
  estado text default 'activa' check (estado in ('activa','inactiva'))
);`
}, {
  name: 'gas_ventas',
  label: 'Gas · Ventas',
  sql: `create table if not exists gas_ventas (
  id text primary key,
  numero_factura text not null,
  cliente_id text references gas_clientes(id) on delete cascade,
  cliente_nombre text default '',
  fecha date default current_date,
  cantidad numeric default 0,
  precio numeric default 0,
  total numeric default 0,
  ruta text default '',
  supervisor text default '',
  observaciones text default ''
);`
}];

// Política RLS de acceso anónimo (prototipo) para cada tabla del modelo.
// EXCEPCIÓN: 'usuarios' nunca usa esta política — ver RLS_USUARIOS_SQL.
const TABLAS_SIN_RLS_ABIERTO = new Set(['usuarios']);
const rlsFor = (t: string) => `alter table ${t} enable row level security;

create policy "${t} acceso anon"
  on ${t} for all
  to anon
  using (true)
  with check (true);`;

// RLS estricto para 'usuarios': RLS habilitado y SIN ninguna política para
// el rol anon. Esto bloquea por completo select/insert/update/delete desde
// el navegador con la anon key. El único acceso a esta tabla es a través de
// las Edge Functions 'login' y 'guardar-usuario', que usan la service_role
// key (solo disponible en el servidor de Supabase).
export const RLS_USUARIOS_SQL = `alter table usuarios enable row level security;

-- A propósito: ninguna policy para 'anon'. Si ya existe una política vieja
-- de "acceso anon" sobre usuarios (de una versión anterior del esquema),
-- bórrala primero con:
-- drop policy if exists "usuarios acceso anon" on usuarios;`;

// SQL completo del esquema: crea las 7 tablas + sus políticas, en orden seguro
// de dependencias (tipos_sucursal y sucursales antes de consumo). La tabla
// 'usuarios' recibe RLS estricto (RLS_USUARIOS_SQL) en vez del RLS abierto.
export const SCHEMA_SQL = MODEL_TABLES.map(t => t.sql).join('\n\n') + '\n\n' + MODEL_TABLES.filter(t => !TABLAS_SIN_RLS_ABIERTO.has(t.name)).map(t => rlsFor(t.name)).join('\n\n') + '\n\n' + RLS_USUARIOS_SQL;

// Datos semilla para el catálogo de tipos de sucursal. Coincide exactamente
// con los tipos reales del negocio: estaciones de gas/gasolina y oficina.
export const TIPOS_SUCURSAL_SEED_SQL = `insert into tipos_sucursal (id, nombre, orden) values
('t1', 'Estacion de gasolina', 1),
('t2', 'Estacion de gas (GLP)', 2),
('t3', 'Oficina', 3)
on conflict (id) do nothing;`;

// Cámbialas antes de usar esto en producción real (ver módulo Usuarios).
export const USUARIOS_SEED_SQL = `insert into usuarios (id, nombre, correo, password_hash, rol, sucursales_ids, archivado) values
('u-admin', 'Administrador GETR', 'admin@getr.do', '$2b$10$YQ8rgFZCOvqa7et5bTM88eVeld0xPjjAdQKg6RYvPB298CPDHY1vy', 'admin', '[]'::jsonb, false),
('u-sup1', 'Ana Rodriguez', 'ana@getr.do', '$2b$10$Nt9E0d4OOhyz4YaQUys5LuWj5JxzxuRGAJCHVAV3F5z5MpcEAKzPm', 'supervisor', '["s1"]'::jsonb, false),
('u-tec1', 'Pedro Vargas', 'pedro@getr.do', '$2b$10$HnjBR5PS4y9UOK4XMxypPeUeXzDA3PPXXwl.RbVhPB7LNNyJUbl9y', 'tecnico', '["s2","s3"]'::jsonb, false)
on conflict (id) do nothing;`;

// Estado de existencia de cada tabla del modelo.
export async function getTableStatus(table: string): Promise<ConnectionStatus> {
  try {
    const {
      error
    } = await supabase.from(table).select('*').limit(1);
    return classifyError(error);
  } catch {
    return 'offline';
  }
}
export async function getModelStatus(): Promise<Record<string, ConnectionStatus>> {
  const entries = await Promise.all(MODEL_TABLES.map(async t => [t.name, await getTableStatus(t.name)] as const));
  return Object.fromEntries(entries);
}